import {
  TaskFilterType,
  TaskParentContainerType,
  TaskStatus,
  TaskType,
  TaskViewer,
} from '../enums/deprecated-task.enum';
import { TaskParentFileInfo } from './file.model';
import { Origin } from './origin.model';
import { TaskParents } from './parents.model';
import { Permission } from './permission.model';

export interface TaskParentReference {
  id: string;
  type: TaskParentContainerType;
  label?: string;
  permissions?: Permission[];
  project?: string;
  version?: number;
}

interface ContainerParentInfo {
  _id: string;
  label: string;
}

interface TaskParentInfo {
  group?: ContainerParentInfo;
  project?: ContainerParentInfo;
  subject?: ContainerParentInfo;
  session?: ContainerParentInfo;
  acquisition?: ContainerParentInfo;
  analysis?: ContainerParentInfo;
  file?: TaskParentFileInfo;
}

export interface TaskFilter {
  match: string;
  type: TaskFilterType;
}

export interface Task {
  _id: string;
  assignee: string;
  batch_number?: number;
  batch_total_tasks?: number;
  config_overrides?: { [key: string]: {} };
  created?: string;
  due_date?: string;
  filters?: TaskFilter[];
  form_id?: string;
  info?: {};
  item_number?: number;
  modified?: string;
  origin?: Origin;
  parent: TaskParentReference;
  parent_info?: TaskParentInfo;
  parents: TaskParents;
  parent_tag_filter: {
    exclude: string[];
    include: string[];
  };
  protocol_id?: string;
  revision: number;
  status: TaskStatus;
  tags?: string[];
  task_id: string;
  task_type?: TaskType;
  viewer: TaskViewer;
  viewer_config_id?: string;
}

export interface TaskInput {
  assignee: string;
  parent: TaskParentReference;
  status: TaskStatus;
  form_id?: string;
  viewer: TaskViewer;
  viewer_config_id?: string;
  info?: {};
  config_overrides?: {};
  task_type?: TaskType;
  batch_number?: number;
  item_number?: number;
  protocol_id?: string;
  due_date?: string;
  tags?: string[];
}

export interface MatchingContainerCount {
  matching: number;
}

export interface TaskParentsFilter {
  parent_type: TaskParentContainerType;
  project_id: string;
  parent_tag_filter: {
    include?: string[];
    exclude?: string[];
  };
}

export interface TaskParentsEstimateFilter {
  parent_type: TaskParentContainerType;
  project_id: string;
  include_tags?: string[];
  exclude_tags?: string[];
}

interface DryRunTask {
  assignee: string;
  parent: ContainerReference;
  parents?: TaskParents;
  status?: TaskStatus;
  parent_labels?: TaskParents;
  duplicate_count?: number;
}

export interface BulkTaskInput extends TaskParentsFilter {
  assignees: string[];
  viewer: TaskViewer;
  info?: {};
  config_overrides?: {};
  task_type: TaskType;
  tags?: string[];
  protocol_id?: string;
  form_id?: string;
  viewer_config_id?: string;
  due_date?: string;
  allow_duplicates: boolean;
}

export interface BulkTasksDeletePayload {
  task_ids: string[];
}

export interface TaskStatusItem {
  className: string;
  display: string;
  value: TaskStatus;
  isComplete: boolean;
}

interface ContainerReference {
  type: 'site' | 'group' | 'project';
  id: string;
}

export enum TaskFormQuestionType {
  Dropdown = 'dropdown',
  Text = 'text',
  TextField = 'textfield',
  TextArea = 'textarea',
  Radio = 'radio',
  SelectBoxes = 'selectboxes',
}

export interface TaskSubFormResponse {
  value: string | string[];
  [key: string]: Record<string, string | string[]>[] | string | string[];
}

export interface TaskFormQuestion {
  key: string;
  label: string;
  required: boolean;
  type: TaskFormQuestionType;
  values?: {
    label: string;
    value: string;
    measurementTools: string[];
    requireMeasurements: string[];
  }[];
}
export interface TaskFormConfig {
  components: TaskFormQuestion[];
}

export interface TaskDetails {
  reader_task: Task;
  viewer_config?: {
    _id: string;
    name: string;
    config: {};
    created: string;
    modified: string;
    deleted: string;
    revision: number;
  };
  form: {
    _id: string;
    origin: Origin;
    viewer: 'OHIF' | 'Form';
    parent: ContainerReference;
    parents: {
      group: string;
      project: string;
    };
    form: {
      studyForm: TaskFormConfig;
    };
    created: string;
    modified: string;
    deleted?: string;
    revision: number;
  };
  form_response?: {
    _id: string;
    origin: Origin;
    form_id: string;
    parent: ContainerReference;
    parents: {
      group: string;
      project: string;
    };
    task_id: string;
    response_data: Record<string, string | string[] | TaskSubFormResponse>;
    created: string;
    modified: string;
    deleted?: string;
    revision: number;
  };
  annotations: {
    _id: string;
    origin: Origin;
    file_ref: {
      file_id: string;
      version: number;
    };
    task_id: string;
    data: FormAnnotationData;
    created: string;
    modified: string;
    deleted: string;
    revision: number;
  }[];
}

interface FormAnnotationData {
  _id: string;
  toolType: string;
  uuid: string;
  StudyInstanceUID: string;
  SeriesInstanceUID: string;
  SOPInstanceUID: string;
  frameIndex: number;
  lesionNamingNumber: number;
  location: string;
  flywheelOrigin: Origin;
  measurementNumber: number;
  imagePath: string;
  handles: {
    start: {
      x: number;
      y: number;
    };
    end: {
      x: number;
      y: number;
    };
    middle: {
      x: number;
      y: number;
    };
    textBox: {
      x: number;
      y: number;
      boundingBox: {
        width: number;
        height: number;
        left: number;
        top: number;
      };
    };
  };
}

export interface BulkTaskDryrunResult {
  duplicated_tasks: DryRunTask[];
  novel_tasks: Task[];
  counts: {
    duplicated_tasks: number;
    novel_tasks: number;
    total_tasks: number;
  };
}

export interface DryrunTaskDeletionPreview {
  _id: string;
  label: string;
  container: string;
  assignee: string;
  due_date: string;
  status: TaskStatus;
}

export enum TaskListColumn {
  Project = 'project',
  TaskId = 'task_id',
  TaskType = 'task_type',
  Container = 'container',
  Assignee = 'assignee',
  Creator = 'creator',
  Created = 'created',
  DueDate = 'due',
  Status = 'status',
  Tag = 'tag',
}
